import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { useState, useContext } from "react";
import classNames from "classnames";
import { gql } from "@apollo/client";
import CiroSpinner from "../../shared/CiroSpinner";
import {
  CiroDropdownButton,
  CiroDropdownButtonItem,
} from "../../shared/CiroDropdownButton";
import useHandleDownloadAutopilotContacts from "../../../reactHooks/autopilotList/contactList/useHandleDownloadAutopilotContacts";
import AutopilotSessionWebhookExportModal from "./AutopilotSessionWebhookExportModal/AutopilotSessionWebhookExportModal";
import AutopilotSessionAddToContactListModal from "./AutopilotSessionAddToContactListModal";
import AutopilotReviewContactsContext from "../../../contexts/AutopilotReviewContactsContext";
import { AutopilotSessionExportButton_PermissionsFragment } from "../../../__generated__/graphql";

export const AutopilotSessionExportButton_Permissions = gql`
  fragment AutopilotSessionExportButton_Permissions on Permissions {
    canExportToWebhook
  }
`;

const AutopilotSessionExportButton = ({
  autopilotSessionId,
  autopilotSessionName,
  permissions,
}: {
  autopilotSessionId: number;
  autopilotSessionName: string | null;
  permissions?: AutopilotSessionExportButton_PermissionsFragment | null;
}) => {
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const { handleDownloadContacts, isDownloading } =
    useHandleDownloadAutopilotContacts();
  const { selectedContactIdsToOrgContactIds } = useContext(
    AutopilotReviewContactsContext,
  );

  const contactIds = Array.from(selectedContactIdsToOrgContactIds.values());
  const contactCount = contactIds.length;
  const hasSelectedContacts = selectedContactIdsToOrgContactIds.size > 0;

  const canExportToWebhook = permissions?.canExportToWebhook ?? false;

  const handleCsvExport = () => {
    handleDownloadContacts({
      autopilotSessionId,
      autopilotSessionName: autopilotSessionName ?? undefined,
      customContactIds: contactIds,
    });
  };

  return (
    <>
      <CiroDropdownButton
        label={
          <div
            className={classNames(
              "flex",
              "flex-row",
              "gap-2",
              "items-center",
              "text-gray-600",
            )}
          >
            {isDownloading ? (
              <CiroSpinner loading={true} />
            ) : (
              <ArrowUpTrayIcon className={classNames("w-4", "h-4")} />
            )}
            <span className={classNames("hidden", "md:flex")}>
              Export {contactCount > 0 ? `(${contactCount})` : ""}
            </span>
          </div>
        }
        disabled={isDownloading}
        customButtonClassName={classNames("py-3", "text-sm", "text-gray-700")}
      >
        {hasSelectedContacts && (
          <CiroDropdownButtonItem
            analyticsField="Save to List"
            onClick={() => setIsAddToListModalOpen(true)}
            disabled={isDownloading}
          >
            Save to list
          </CiroDropdownButtonItem>
        )}
        <CiroDropdownButtonItem
          analyticsField="Export to CSV"
          onClick={handleCsvExport}
          disabled={isDownloading}
        >
          Export to CSV
        </CiroDropdownButtonItem>
        {canExportToWebhook && (
          <CiroDropdownButtonItem
            analyticsField="Export to Webhook"
            onClick={() => setIsWebhookModalOpen(true)}
            disabled={isDownloading}
          >
            Export to webhook
          </CiroDropdownButtonItem>
        )}
      </CiroDropdownButton>

      <AutopilotSessionWebhookExportModal
        isOpen={isWebhookModalOpen}
        onClose={() => setIsWebhookModalOpen(false)}
        autopilotSessionId={autopilotSessionId}
        contactIds={contactIds}
      />

      <AutopilotSessionAddToContactListModal
        isOpen={isAddToListModalOpen}
        onClose={() => setIsAddToListModalOpen(false)}
        contactCount={contactCount}
      />
    </>
  );
};

export default AutopilotSessionExportButton;
